import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { logDebug } from '../../utils/logger';

export interface RumStatsResult {
  success: boolean;
  days?: number;
  pageViews?: number;
  sessions?: number;
  jsErrors?: number;
  httpErrors?: number;
  avgLcpMs?: number;
  error?: string;
}

export type CardState = 'loading' | 'data' | 'nodata' | 'error' | 'nonprod';

const PRODUCTION_HOSTNAME = 'tumbyfc.com';
// The live site is served on www.tumbyfc.com (the apex redirects to www), so
// both must count as production — otherwise the card shows the "production
// only" note while the admin is actually on the live site.
const PRODUCTION_HOSTNAMES = ['tumbyfc.com', 'www.tumbyfc.com'];

/**
 * Pure state-selection logic for the Website Activity card (design.md
 * Property 1): a failed call can never resolve to a data/nodata grid, and
 * "no data yet" is never presented as an error.
 *
 * - `null` result (still in flight) is handled by the caller via `loading`;
 *   this function is only called once a result exists.
 * - `success: false` always maps to `error`, regardless of hostname.
 * - Off-production-domain (and successful) maps to `nonprod`, so admins on
 *   dev.tumbyfc.com see a "production only" note rather than "no activity".
 * - On production, all metrics zero/absent maps to `nodata`.
 * - On production, any metric non-zero maps to `data`.
 */
export function deriveCardState(result: RumStatsResult | null, hostname: string): CardState {
  if (!result) return 'loading';
  if (!result.success) return 'error';

  const isProd = PRODUCTION_HOSTNAMES.includes(hostname);
  const hasActivity =
    (result.pageViews || 0) > 0 ||
    (result.sessions || 0) > 0 ||
    (result.jsErrors || 0) > 0 ||
    (result.httpErrors || 0) > 0;

  if (!isProd) return 'nonprod';
  return hasActivity ? 'data' : 'nodata';
}

const WebStatsCard: React.FC = () => {
  const [result, setResult] = useState<RumStatsResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const client = generateClient({ authMode: 'userPool' });
        const res: any = await (client.queries as any).getRumStats({ days: 7 });
        logDebug('WebStatsCard', 'raw response:', res);
        let data = res?.data;
        // Handle double-stringification from AppSync + Lambda JSON.stringify
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { /* not JSON */ }
        }
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch { /* not JSON */ }
        }
        if (res?.errors?.length) throw new Error(res.errors[0].message || 'GraphQL error');
        if (!data?.success) throw new Error(data?.error || 'Failed to load stats');
        if (!cancelled) {
          setResult({
            success: true,
            days: data.days,
            pageViews: data.pageViews || 0,
            sessions: data.sessions || 0,
            jsErrors: data.jsErrors || 0,
            httpErrors: data.httpErrors || 0,
            avgLcpMs: data.avgLcpMs || 0,
          });
        }
      } catch (err: any) {
        console.warn('[WebStatsCard]', err.message || err);
        if (!cancelled) {
          setResult({ success: false, error: err.message || 'Failed to load stats' });
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const cardState = deriveCardState(result, window.location.hostname);

  const cell = (label: string, value: number | string, tone: 'default' | 'warn' = 'default') => (
    <div className={`bg-white rounded-lg shadow p-4 ${tone === 'warn' && typeof value === 'number' && value > 0 ? 'border-l-4 border-amber-400' : ''}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
    </div>
  );

  return (
    <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6 mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Website Activity (last 7 days)</h3>
        <a href="https://ap-southeast-2.console.aws.amazon.com/cloudwatch/home?region=ap-southeast-2#rum:overview" target="_blank" rel="noopener noreferrer" className="text-xs text-fishing-blue hover:underline">Full analytics →</a>
      </div>

      {cardState === 'loading' && (
        <div className="text-sm text-gray-500">Loading…</div>
      )}

      {cardState === 'error' && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Could not load website statistics{result?.error ? `: ${result.error}` : '.'}
        </div>
      )}

      {cardState === 'nonprod' && (
        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
          Website statistics are collected on the live site ({PRODUCTION_HOSTNAME}) only.
        </div>
      )}

      {cardState === 'nodata' && (
        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
          No activity recorded yet. It can take a little while after launch for visits to show up here.
        </div>
      )}

      {cardState === 'data' && result && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {cell('Page Views', result.pageViews || 0)}
          {cell('Sessions', result.sessions || 0)}
          {cell('JS Errors', result.jsErrors || 0, 'warn')}
          {cell('HTTP Errors', result.httpErrors || 0, 'warn')}
          {cell('Average LCP', `${result.avgLcpMs || 0} ms`)}
        </div>
      )}
    </div>
  );
};

export default WebStatsCard;
